import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const paymentKey = searchParams.get('paymentKey');
  const orderId = searchParams.get('orderId');
  const amount = searchParams.get('amount');

  const [confirming, setConfirming] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    if (!paymentKey || !orderId || !amount) {
      setError('결제 정보가 올바르지 않습니다.');
      setConfirming(false);
      return;
    }

    const confirmPayment = async () => {
      try {
        const response = await fetch('/api/v1/payments/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            paymentKey,
            orderId,
            amount: Number(amount),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || '결제 승인에 실패했습니다.');
        }

        setSuccess(true);
        setResult(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setConfirming(false);
      }
    };

    confirmPayment();
  }, [paymentKey, orderId, amount]);

  if (confirming) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">결제 승인 중</h2>
          <p className="text-gray-600">잠시만 기다려주세요...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">결제 승인 실패</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    const isCertificateIssue = result?.type === 'CERTIFICATE_ISSUE';

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">결제가 완료되었습니다</h2>

          {isCertificateIssue ? (
            <>
              <p className="text-gray-600 mb-2">
                인증서가 성공적으로 발급되었습니다!
              </p>
              <p className="text-sm text-gray-500 mb-6">
                발급 인원: {result.issuedCount}명
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/provider/posts')}
                  className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  수업 목록으로 이동
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-6">
                인증서를 확인할 수 있습니다.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/certificates/${result.certificateId}/view`)}
                  className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  인증서 보기
                </button>
                <button
                  onClick={() => navigate('/profile/certificates')}
                  className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  인증서 보관함으로 이동
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
}
